import { white } from '@synoptic/ui-kit/palette.js';
import { responsiveStyle } from '@synoptic/ui-kit/responsive/responsive-style.js';
import { hairline } from '@synoptic/ui-kit/theme-v2/hairline.js';
import { themeV2 } from '@synoptic/ui-kit/theme-v2/vars.js';
import { borderRadius, fontWeight, space } from '@synoptic/ui-kit/tokens.js';
import { typography } from '@synoptic/ui-kit/typography.js';
import { globalStyle, style } from '@vanilla-extract/css';
import { pageInsetV2 } from '../_app/page-inset-v2.css.ts';

const streamCardMinWidth = 304;

export const explorePageV2 = style([
  pageInsetV2,
  {
    display: 'flex',
    flexDirection: 'column',
    gap: space.s32,
    minWidth: 0,
  },
  responsiveStyle({
    tablet: {
      gap: space.s64,
    },
  }),
]);

export const exploreSectionV2 = style({
  display: 'flex',
  flexDirection: 'column',
  gap: space.s20,
  minWidth: 0,
});

export const exploreSectionHeaderV2 = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: space.s16,
  minWidth: 0,
});

export const exploreSectionHeadingV2 = style({
  display: 'flex',
  flexDirection: 'column',
  gap: space.s8,
  minWidth: 0,
});

export const exploreSectionTitleV2 = style({
  margin: 0,
  fontWeight: fontWeight.medium,
  fontSize: 28,
  lineHeight: 1.3,
  letterSpacing: '-0.03em',
  color: themeV2.color.text.primary,
  ...responsiveStyle({
    tablet: {
      fontSize: 36,
    },
  }),
});

export const exploreSectionSubtitleV2 = style({
  margin: 0,
  fontSize: 14,
  fontWeight: fontWeight.regular,
  lineHeight: 1.4,
  color: themeV2.color.text.quaternary,
});

export const exploreCardGridV2 = style({
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${streamCardMinWidth}px), 1fr))`,
  gap: space.s16,
  minWidth: 0,
});

export const exploreCardSkeletonV2 = style({
  minHeight: 177,
  width: '100%',
  borderRadius: borderRadius.r16,
});

export const exploreDataStreamsListV2 = style({
  gap: space.s16,
});

export const exploreDataStreamFiltersV2 = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: space.s8,
  width: '100%',
  minWidth: 0,
  flexWrap: 'wrap',
});

export const exploreDataStreamRowsV2 = style({
  display: 'flex',
  flexDirection: 'column',
  gap: space.s8,
  minWidth: 0,
});

export const exploreDataStreamRowSkeletonV2 = style({
  minHeight: 62,
  width: '100%',
  borderRadius: borderRadius.r16,
});

export const exploreCustomCardSkeletonV2 = style([
  exploreCardSkeletonV2,
  { minHeight: 238.58 },
]);

const packageCardMinWidth = 440;

export const explorePackageCardGridV2 = style({
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${packageCardMinWidth}px), 1fr))`,
  gap: space.s20,
  minWidth: 0,
});

export const explorePackageCardSkeletonV2 = style([
  exploreCardSkeletonV2,
  { minHeight: 540 },
]);

export const exploreHowCardGridV2 = style({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  gap: space.s16,
  minWidth: 0,
  ...responsiveStyle({
    tablet: {
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    },
  }),
});

export const exploreHowCardV2 = style({
  position: 'relative',
  gap: space.s16,
  height: '100%',
});

export const exploreHowCoverV2 = style({
  position: 'relative',
  display: 'block',
  width: '100%',
  height: 131,
  flexShrink: 0,
  overflow: 'hidden',
  borderRadius: borderRadius.r8,
  backgroundColor: '#0F1116',
});

globalStyle(`.${exploreHowCoverV2}::after`, {
  content: '',
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  pointerEvents: 'none',
  borderRadius: 'inherit',
  boxShadow: hairline(white[50]),
});

export const exploreHowCoverImageV2 = style({
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'contain',
});

export const exploreHowCardBodyV2 = style({
  gap: space.s8,
});

export const exploreHowCardTitleV2 = style({
  fontWeight: fontWeight.medium,
});

export const exploreHowWatchButtonV2 = style({
  ...typography.labelMd,
  appearance: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: space.s8,
  width: 'fit-content',
  margin: 0,
  padding: 0,
  border: 0,
  background: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  color: themeV2.color.text.secondary,
  selectors: {
    '&::after': {
      content: '',
      position: 'absolute',
      inset: 0,
      zIndex: 1,
    },
    '&:focus': {
      outline: 'none',
    },
    '&:focus-visible': {
      outline: 'none',
    },
    '&:focus-visible::after': {
      outline: `2px solid ${themeV2.color.border.focus}`,
      outlineOffset: '-1px',
    },
  },
});

globalStyle(`.${exploreHowWatchButtonV2} :where(svg)`, {
  width: 16,
  height: 16,
  flexShrink: 0,
});
